import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
} from '@groundworks/ngx-groundworks-ui';
import { NgSelectModule } from '@ng-select/ng-select';
import { Observable } from 'rxjs';
import {
  CreatePpjExceptionDto,
  PpjExceptionPersonGroup,
  PpjExceptionPersonResponse,
  PpjExceptionResponse,
  UpdatePpjExceptionDto,
} from '../../sdk/sdk';
import { PpjService } from '../../services/ppj.service';
import { commissionErrorMessage } from '../../utils/commission-error-message';
import { formatUtcIsoDate, parseLocalIsoDate } from '../../utils/iso-date';

// MOD-4649 — writable signals rather than input(), matching the house modal contract: every modal
// in this repo is opened by ModalService and populated imperatively via
// `modalRef.componentInstance.<x>.set(...)` (see openPpjExceptionModal, modal.service.ts). An
// InputSignal has no .set(), so input.required<string>() would not be settable by that factory.
@Component({
  selector: 'app-ppj-exception-modal',
  imports: [
    NgSelectModule,
    ReactiveFormsModule,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
  ],
  templateUrl: './ppj-exception-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PpjExceptionModalComponent implements AfterViewInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly ppjService = inject(PpjService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = signal('');
  // Null when adding, the existing row when editing.
  readonly exception = signal<PpjExceptionResponse | null>(null);

  readonly saved = output<void>();
  readonly cancelled = output<void>();

  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly people = signal<PpjExceptionPersonResponse[]>([]);
  // T9 — hasBranch: false and (hasBranch: true, people: []) both arrive as an empty list, so the
  // flag is held separately: it is the only thing that keeps "this job has no branch" apart from
  // "nobody at the branch is eligible". Optimistic default, replaced by the first response.
  readonly hasBranch = signal(true);
  // A failed fetch also yields an empty list, and letting it fall through to "nobody is eligible"
  // would state a payroll fact we have not established. Same reasoning as R14, one state further out.
  readonly peopleError = signal(false);

  readonly isEdit = computed(() => this.exception() !== null);
  readonly hasEligiblePeople = computed(() => this.hasBranch() && this.people().length > 0);

  readonly title = computed(() => (this.isEdit() ? 'Edit PPJ Exception' : 'Add PPJ Exception'));

  // Cancel first, then the primary — the ordering the rest of the groundworks-modal-footer call
  // sites use. Save carries the same gate the old inline submit button did.
  readonly footerActions = computed<ModalFooterAction[]>(() => [
    {
      label: 'Cancel',
      type: 'outline-secondary',
      action: () => this.cancel(),
    },
    {
      label: 'Save Exception',
      type: 'primary',
      action: () => this.submit(),
      loading: () => this.saving(),
      disabled: () => this.form.invalid || this.saving(),
    },
  ]);

  // The picker is disabled in edit mode, but it still has to show who the exception belongs to —
  // and a since-deactivated person is filtered out of the eligible list while their exception
  // lives on (R7). Seeding the row's own person keeps that name on screen instead of a blank box.
  //
  // The seeded row's label is the bare display name: the exception carries no Workday id, and
  // rendering the "(------)" placeholder here would state that this person has no id on record
  // when the truth is that this response never carried one. Eligible people, who do come with a
  // server-formatted label, keep it.
  readonly pickerItems = computed<PpjExceptionPersonResponse[]>(() => {
    const existing = this.exception();
    const eligible = this.people();
    if (!existing?.userId || eligible.some((person) => person.id === existing.userId)) {
      return eligible;
    }
    return [
      new PpjExceptionPersonResponse({
        id: existing.userId,
        displayName: existing.userDisplayName,
        label: existing.userDisplayName,
        group: PpjExceptionPersonGroup.ProjectAssociated,
      }),
      ...eligible,
    ];
  });

  readonly ppjGroupLabel = (person: PpjExceptionPersonResponse): string =>
    person.group === PpjExceptionPersonGroup.Branch ? 'Branch Employees' : 'Recommended';

  readonly form = this.formBuilder.group({
    userId: this.formBuilder.control<string | null>(null, Validators.required),
    amount: this.formBuilder.control<number | null>(null, Validators.required),
    // R13 — an absent or unparseable Date Qualified is rejected here as well as at the boundary.
    dateQualified: this.formBuilder.control<string | null>(null, Validators.required),
    note: this.formBuilder.control<string | null>(null),
  });

  constructor() {
    effect(() => this.loadPeople(this.projectId()));
    effect(() => this.prefillForm(this.exception()));
  }

  // The person lock lives here rather than in the effect above, and the timing is load-bearing.
  // FormGroupDirective recomputes the whole tree the first time `[formGroup]` binds
  // (forms.mjs:5278), and FormGroup.value drops disabled children — so a lock applied before that
  // binding takes the person straight back out of the form's value, on a form whose whole job is
  // to show whose exception is being edited. Locking afterwards leaves the group's cached value
  // alone (`onlySelf` keeps it that way, forms.mjs:2332) while the control is genuinely disabled:
  // the picker cannot be edited and submit() never reads it. ModalService builds a fresh instance
  // per open and sets `exception` before the first render, so one pass here covers every case.
  ngAfterViewInit(): void {
    if (this.isEdit()) {
      this.form.controls.userId.disable({ onlySelf: true, emitEvent: false });
    }
  }

  submit(): void {
    if (this.form.invalid) {
      return;
    }
    this.errorMessage.set(null);
    this.saving.set(true);

    this.saveRequest().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving.set(false);
        this.saved.emit();
      },
      error: (err: unknown) => {
        this.saving.set(false);
        this.errorMessage.set(commissionErrorMessage(err));
      },
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  private saveRequest(): Observable<void> {
    const value = this.form.getRawValue();
    const dateQualified = parseLocalIsoDate(value.dateQualified ?? '');
    const existing = this.exception();

    // UpdatePpjExceptionDto carries no userId — the person is fixed once the row exists, which is
    // why the control is locked rather than merely ignored here.
    return existing?.id
      ? this.ppjService.updateException(existing.id, new UpdatePpjExceptionDto({
          amount: value.amount ?? undefined,
          dateQualified,
          note: value.note ?? undefined,
        }))
      : this.ppjService.createException(this.projectId(), new CreatePpjExceptionDto({
          userId: value.userId ?? undefined,
          amount: value.amount ?? undefined,
          dateQualified,
          note: value.note ?? undefined,
        }));
  }

  private loadPeople(projectId: string): void {
    this.ppjService
      .getExceptionPeople(projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.peopleError.set(false);
          this.hasBranch.set(response.hasBranch === true);
          this.people.set(response.people ?? []);
        },
        error: () => {
          this.peopleError.set(true);
          this.people.set([]);
        },
      });
  }

  private prefillForm(existing: PpjExceptionResponse | null): void {
    this.form.reset({
      userId: existing?.userId ?? null,
      amount: existing?.amount ?? null,
      dateQualified: existing?.dateQualified ? formatUtcIsoDate(existing.dateQualified) : null,
      note: existing?.note ?? null,
    });
  }
}
